import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/auth';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(email, password);
          const { access_token } = response;

          // Store token
          localStorage.setItem('authToken', access_token);

          // Fetch user profile
          const user = await authService.getProfile();

          set({
            token: access_token,
            user,
            isAuthenticated: true,
            isLoading: false,
          });

          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Login failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      register: async (email, password, fullName) => {
        set({ isLoading: true, error: null });
        try {
          await authService.register(email, password, fullName);
          
          // Auto-login after registration
          const result = await get().login(email, password);
          return result;
        } catch (error) {
          const message = error.response?.data?.detail || 'Registration failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      logout: async () => {
        try {
          await authService.logout();
        } finally {
          localStorage.removeItem('authToken');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
        }
      },

      fetchProfile: async () => {
        try {
          const user = await authService.getProfile();
          set({ user });
          return user;
        } catch (error) {
          console.error('Failed to fetch profile:', error);
          return null;
        }
      },

      updateProfile: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const user = await authService.updateProfile(data);
          set({ user, isLoading: false });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Update failed';
          set({ error: message, isLoading: false });
          return { success: false, error: message };
        }
      },

      clearError: () => set({ error: null }),

      // Initialize from stored token
      initialize: async () => {
        const token = localStorage.getItem('authToken');
        if (token) {
          set({ token, isAuthenticated: true, isLoading: true });
          try {
            const user = await authService.getProfile();
            set({ user, isLoading: false });
          } catch {
            // Token invalid, clear it
            localStorage.removeItem('authToken');
            set({ token: null, isAuthenticated: false, isLoading: false });
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Initialize on load
if (typeof window !== 'undefined') {
  useAuthStore.getState().initialize();
}
